/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED, } from "../../../../share/util/api/polling";
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { PHASE, HEADER_PHASE_INFO, PHASE_INFO_FOR_ROUTINE, PHASE_INFO_FOR_MONITOR, ASYNC_ERROR_INFO } from './constants';
import error from '../../../../common/nodejs/errors';

export default {
  name: 'requirement160',
  data() {
    return {
      phase: PHASE.WAIT,
      isLoading: false,
      isDispose: true,
      systemId: '',
      modal_requirement_160: false,
      exitPromise: null
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /** ダイアログのヘッダー色 */
    headerColor() { return HEADER_PHASE_INFO[this.phase]['headerColor']; },
    /** ダイアログのヘッダータイトル */
    headerTitle() { return this.$t(HEADER_PHASE_INFO[this.phase]['headerTitle']); },

    /** ダイアログの表示文言 */
    workSupportMessage() {
      const messageMap = {
        [PHASE.WAIT]: `${this.i18nWsKey}.REQ_WS_160_TXT_1`,
        [PHASE.RL_EXE]: `${this.i18nWsKey}.REQ_WS_160_TXT_2`,
        [PHASE.RL_MONITOR]: `${this.i18nWsKey}.REQ_WS_160_TXT_3`,
        [PHASE.RR_EXE]: `${this.i18nWsKey}.REQ_WS_160_TXT_4`,
        [PHASE.RR_MONITOR]: `${this.i18nWsKey}.REQ_WS_160_TXT_3`,
        [PHASE.AIR_EXE]: `${this.i18nWsKey}.REQ_WS_160_TXT_5`,
        [PHASE.AIR_MONITOR]: `${this.i18nWsKey}.REQ_WS_160_TXT_3`,
        [PHASE.TMC_MONITOR]: `${this.i18nWsKey}.REQ_WS_160_TXT_6`,
        [PHASE.PIPE_EXE]: `${this.i18nWsKey}.REQ_WS_160_TXT_7`,
        [PHASE.PIPE_MONITOR]: `${this.i18nWsKey}.REQ_WS_160_TXT_3`,
        [PHASE.STOP]: `${this.i18nWsKey}.REQ_WS_160_TXT_8`,
        [PHASE.EXECUTION_CONDITION_ERROR]: `${this.i18nWsKey}.REQ_WS_160_TXT_9`,
        [PHASE.START_ERROR]: `${this.i18nWsKey}.REQ_WS_160_TXT_10`,
        [PHASE.AIR_BLEEDING_ERROR]: `${this.i18nWsKey}.REQ_WS_160_TXT_11`,
        [PHASE.TEST_ERROR]: `${this.i18nWsKey}.REQ_WS_160_TXT_12`,
        [PHASE.BRAKE_ERROR]: `${this.i18nWsKey}.REQ_WS_160_TXT_13`,
        [PHASE.PIPE_ERROR]: `${this.i18nWsKey}.REQ_WS_160_TXT_14`,
        [PHASE.FAILED]: 'individual.error_communication_error',
      };
      return this.$t(messageMap[this.phase]);
    },

    /** OKボタンの表示有無 */
    showButtonOk() {
      const showOkButtonMap = {
        [PHASE.WAIT]: true,
        [PHASE.RL_EXE]: true,
        [PHASE.RR_EXE]: true,
        [PHASE.AIR_EXE]: true,
        [PHASE.PIPE_EXE]: true,
        [PHASE.STOP]: true,
        [PHASE.EXECUTION_CONDITION_ERROR]: true,
        [PHASE.START_ERROR]: true,
        [PHASE.AIR_BLEEDING_ERROR]: true,
        [PHASE.TEST_ERROR]: true,
        [PHASE.BRAKE_ERROR]: true,
        [PHASE.PIPE_ERROR]: true,
        [PHASE.FAILED]: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /** キャンセルボタンの表示有無 */
    showButtonCancel() {
      return this.phase === PHASE.WAIT;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_160 = true;
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 待機処理 */
    async read() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          async (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        this.isDispose = response.data.user_data.is_dispose;

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            // 実行(RLフルード交換/エア抜き)へ遷移
            this.phase = PHASE.RL_EXE;
            break;
          // 異常終了  
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_160_EXECUTION_CONDITIONS_FAILED:
                // 条件不成立表示へ遷移
                await this.errorProcess(PHASE.EXECUTION_CONDITION_ERROR);
                break;
              case error.WSP_160_CANNOT_START_FAILED:
                // 開始不可表示へ遷移
                await this.errorProcess(PHASE.START_ERROR);
                break;
              case error.WSP_GENERAL_ERROR:
              default:
                // 共通エラーへ遷移
                await this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 共通エラーへ遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 */
    async execute() {
      try {
        this.isLoading = true;

        const apiType = PHASE_INFO_FOR_ROUTINE[this.phase]['apiType'];
        this.phase = PHASE_INFO_FOR_ROUTINE[this.phase]['monitorPhase'];

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: `execute-${apiType}`
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          async (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result); },
        );

        if (!response) {
          // pollingWspApiはエラーコールバックの終了を待たずnullを返す
          // 非同期処理を含んでいるshowUnexpectedErrorが呼ばれた場合、スピナー消えてしまうためPromiseの解決を待つ
          if (this.exitPromise) await this.exitPromise;
          return;
        }

        switch (response.data.status) {
          // 正常終了
          case REQUEST_STATUS_COMPLETED:
            switch (this.phase) {
              case PHASE.AIR_MONITOR:
                // 現在のフェーズが実行監視(エア混入確認)：再度実行処理(TMC Test)を実行
                await this.execute();
                break;
              case PHASE.PIPE_MONITOR:
                // 現在のフェーズが実行監視(パイプ接続確認)：停止処理を実行
                await this.stopProcess();
                break;
              default:
                // 上記以外：次の実行画面へ遷移
                this.phase = PHASE_INFO_FOR_MONITOR[this.phase]['nextPhase'];
                break;
            }
            break;
          // 異常終了
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_160_AIR_VENT_FAILED:
              case error.WSP_160_BREAK_ABNORMAL_FAILED:
              case error.WSP_160_PIPE_ABNORMAL_FAILED:
              case error.WSP_160_AIR_ENTERS_FAILED:
                // 各種エラー表示へ遷移
                await this.errorProcess(ASYNC_ERROR_INFO[response.data.async_code]['nextPhase']);
                break;
              case error.WSP_GENERAL_ERROR:
              default:
                // 共通エラーへ遷移
                await this.showUnexpectedError();
                break;
            }
            break;
          default:
            // 共通エラーへ遷移
            await this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** terminate処理 */
    async terminate() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'terminate'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result, 'terminate'); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result, 'terminate'); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /** dispose処理 */
    async dispose() {
      try {
        this.isLoading = true;

        // バックエンドAPI POST,GET
        // ここではAPIの実行のみ
        const token = session.getToken();
        await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'dispose'
          },
          // エラー発生のコールバックを実装
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result, "dispose"); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          async (result) => { this.validateErrorResult(result, "dispose"); },
        );
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     * @param {string} calledFrom 呼び出し元
     */
    validateErrorResult(result, calledFrom = '') {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => {
          result;
          // terminate,disposeから呼ばれた場合は共通エラーに遷移しない
          if (calledFrom === 'terminate' || calledFrom === 'dispose') return;
          this.showUnexpectedError();
        },
      );
    },

    /** 終了/dispose処理 */
    async exitProcess() {
      // スピナー消えてしまうのを防ぐためPromiseを保持し、各API失敗時に解決を待つ
      this.exitPromise = this.isDispose ? this.dispose() : this.terminate();
      await this.exitPromise;
    },

    /** 停止遷移時の処理 */
    async stopProcess() {
      await this.exitProcess();
      this.phase = PHASE.STOP;
    },

    /** エラー時の処理
     * @param {String} phase フェーズ名
     */
    async errorProcess(phase) {
      await this.exitProcess();
      this.phase = phase;
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      await this.exitProcess();
      this.phase = PHASE.FAILED;
    },

    /** OKボタン押下時の処理 */
    onOkClicked() {
      switch (this.phase) {
        // 待機
        case PHASE.WAIT:
          this.read();
          break;
        // 実行
        case PHASE.RL_EXE:
        case PHASE.RR_EXE:
        case PHASE.AIR_EXE:
        case PHASE.PIPE_EXE:
          this.execute();
          break;
        default:
          this.closeWizard();
          break;
      }
    },

    /** キャンセルボタン押下時の処理 */
    onCancelClicked() {
      this.closeWizard();
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_160 = false;
    },
  },

  mounted() {
    this.init();
  },
};